import { PagingParameters } from "@/api/dtos";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import {
  artworkColumnsFormatted,
  artworkColumnsVisibility,
  getArtworkForComplexLink,
} from "@/config/dashboard/artwork";
import { generateTable } from "@/config/dashboard/general";
import { FieldConfig } from "@/config/forms/general";
import { Artwork, ArtworkInExhibition, Exhibition } from "@/lib/types";
import { ColumnDef, Row } from "@tanstack/react-table";
import { ChevronLeft, PlusIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import ArtworkInExhibitionDrawer from "./drawer/ArtworkInExhibitionDrawer";

interface ComplexLinkProps {
  id: string;
  artwork: Artwork | undefined;
  exhibition: Exhibition | undefined;
  fields: FieldConfig[] | undefined;
  type: LinkableType;
}

/**
 * ArtworkInExhibitions component
 * @returns {JSX.Element}
 */
const ArtworkInExhibitions = ({
  id,
  artwork,
  exhibition,
  fields,
  type,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: complexeRelation } = useGetComplexRelationFields(
    id,
    "artworkInExhibitions",
    type,
  );

  const { mutate: deleteArtworkInExhibition } = useDeleteComplexLink(
    id,
    "artworkInExhibitions",
    type,
  );
  const [searchContent, setSearchContent] = useState<string>("");

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteArtworkInExhibition({ id, relation: "artworkInExhibition" });
    },
    [deleteArtworkInExhibition],
  );

  const columns: ColumnDef<ArtworkInExhibition, any>[] = useMemo(() => {
    const columnArtwork = getArtworkForComplexLink() as ColumnDef<
      ArtworkInExhibition,
      any
    >[];
    return [
      ...(type === "artwork"
        ? [
            {
              accessorKey: "expositionsDescriptionNumeroExpo",
              header: t("tab_expositionsDescriptionNumeroExpo"),
              enableResizing: true,
              cell: ({ row }: { row: Row<ArtworkInExhibition> }) => {
                return (
                  <TextCell
                    value={
                      row.original.exhibition.expositionsDescriptionNumeroExpo
                    }
                  />
                );
              },
            },
            {
              accessorKey: "artworkInExhbitionVignette",
              header: "",
              size: 100,
              cell: ({ row }: { row: Row<ArtworkInExhibition> }) => {
                return (
                  <ImageCell
                    value={""}
                    attachments={row.original.attachments ?? []}
                    domain={"exhibition"}
                  />
                );
              },
            },
            {
              accessorKey: "expositionsDescriptionAnnee",
              header: t("tab_expositionsDescriptionAnnee"),
              size: 10,
              cell: ({ row }: { row: Row<ArtworkInExhibition> }) => {
                return (
                  <TextCell
                    value={row.original.exhibition.expositionsDescriptionAnnee}
                  />
                );
              },
            },
            {
              accessorKey: "expositionsDescriptionTitre",
              header: t("tab_expositionsDescriptionTitre"),
              size: 300,
              cell: ({ row }: { row: Row<ArtworkInExhibition> }) => {
                return (
                  <HtmlCell
                    value={row.original.exhibition.expositionsDescriptionTitre}
                  />
                );
              },
            },
            {
              accessorKey: "expositionsDescriptionDates",
              header: t("tab_expositionsDescriptionDates"),
              cell: ({ row }: { row: Row<ArtworkInExhibition> }) => {
                return (
                  <TextCell
                    value={row.original.exhibition.expositionsDescriptionDates}
                  />
                );
              },
            },
            {
              accessorKey: "expositionsDescriptionLieu",
              header: t("tab_expositionsDescriptionLieu"),
              size: 300,
              cell: ({ row }: { row: Row<ArtworkInExhibition> }) => {
                return (
                  <div className="flex flex-col items-start text-wrap">
                    <p className="text-[14px] font-[500]">
                      {row.original.exhibition.expositionsDescriptionVille}
                    </p>
                    <p className="text-[14px] font-[400] text-gray-500">
                      {row.original.exhibition.expositionsDescriptionLieu}
                    </p>
                  </div>
                );
              },
            },
          ]
        : columnArtwork.map((column) => ({
            ...column,
            header: typeof column.header === "string" ? t(column.header) : "",
          }))),
      {
        accessorKey: "oeuvresDansExpositionsDescriptionNumeroCatalogue",
        header: t("tab_oeuvresDansExpositionsDescriptionNumeroCatalogue"),
      },
      {
        accessorKey: "oeuvresDansExpositionsNotes",
        header: t("tab_oeuvresDansExpositionsNotes"),
        cell: (props) => {
          return <HtmlCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          const data = row.original as ArtworkInExhibition;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(data.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <ArtworkInExhibitionDrawer
                fields={fields}
                data={data}
                queryId={id}
                type={type}
              />
              {type === "exhibition" && (
                <ShowElements
                  type="artwork"
                  id={row.original.artwork.id}
                  elements={[
                    {
                      label: t("oeuvresDescriptionTitre"),
                      value: row.original.artwork.oeuvresDescriptionTitre,
                      type: "text",
                    },
                    {
                      label: t("oeuvresDescriptionNumeroEnregistrement"),
                      value:
                        row.original.artwork
                          .oeuvresDescriptionNumeroEnregistrement,
                      type: "text",
                    },
                    {
                      label: t("oeuvresDescriptionResume"),
                      value: row.original.artwork.oeuvresDescriptionResume,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.original.artwork.attachments,
                      type: "image",
                    },
                  ]}
                />
              )}
              {type === "artwork" && (
                <ShowElements
                  type="exhibition"
                  id={row.original.exhibition.id}
                  elements={[
                    {
                      label: t("expositionsDescriptionNumeroExpo"),
                      value:
                        row.original.exhibition
                          .expositionsDescriptionNumeroExpo,
                      type: "text",
                    },
                    {
                      label: t("expositionsDescriptionResume"),
                      value:
                        row.original.exhibition.expositionsDescriptionResume,
                      type: "richtext",
                    },
                  ]}
                />
              )}
            </div>
          );
        },
      },
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  const datatableConf = useMemo(() => {
    if (type === "exhibition") {
      return (
        <ExhibitionToArtwork
          id={id}
          relatedData={complexeRelation}
          fields={fields}
          exhibition={exhibition}
          isAddMode={isAddMode}
          columnsLink={columns}
          searchContent={searchContent}
        />
      );
    } else {
      return (
        <ArtworkToExhibition
          id={id}
          relatedData={complexeRelation}
          fields={fields}
          artwork={artwork}
          isAddMode={isAddMode}
          columnsLink={columns}
          searchContent={searchContent}
        />
      );
    }
  }, [
    type,
    id,
    complexeRelation,
    fields,
    exhibition,
    isAddMode,
    columns,
    searchContent,
    artwork,
  ]);

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("relatedRecords")}
          </p>
        </div>
      )}
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">
          {type === "artwork" ? t("exhibitionsRelated") : t("artworksRelated")}
        </h2>
        {!isAddMode && (
          <Button variant="outline" onClick={() => setIsAddMode(true)}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
            {/* <Button variant="default" onClick={() => setIsAddMode(false)}>
              <PlusIcon className="w-4 h-4 mr-2" />
              {t("newArtwork")}
            </Button> */}
          </div>
        )}
      </div>
      {datatableConf}
    </div>
  );
};

export default ArtworkInExhibitions;

type ExhibitionToArtworkProps = LinksTableProps<ArtworkInExhibition> & {
  exhibition: Exhibition | undefined;
};

// Oeuvres liées à une exposition
const ExhibitionToArtwork = ({
  id,
  relatedData,
  fields,
  exhibition,
  isAddMode,
  columnsLink,
  searchContent,
}: ExhibitionToArtworkProps) => {
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notAlreadyRelated } = useGetNotRelatedResource<Artwork>({
    resultType: "artwork",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const columnsArtworks: ColumnDef<Artwork, any>[] = useMemo(() => {
    return [
      ...generateTable(artworkColumnsFormatted, "artwork"),
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Artwork> }) => {
          if (!exhibition) {
            return null;
          }
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ArtworkInExhibitionDrawer
                key={row.original.id}
                fields={fields}
                queryId={exhibition.id}
                type={"exhibition"}
                data={{
                  id: "",
                  artwork: row.original,
                  exhibition: exhibition,
                  oeuvresDansExpositionsDescriptionNumeroCatalogue: "",
                  oeuvresDansExpositionsNotes: "",
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [exhibition, fields]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsArtworks}
          data={notAlreadyRelated?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notAlreadyRelated?.totalSize ?? 0,
          }}
          options={{
            enableColumnResizing: true,
            state: {
              columnVisibility: {
                ...artworkColumnsVisibility,
                oeuvresDescriptionVignette: true,
                oeuvresPublicationStatus: false,
              },
            },
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      isAddMode,
      columnsArtworks,
      columnsLink,
      getPagination,
      notAlreadyRelated?.data,
      notAlreadyRelated?.totalSize,
      relatedData,
    ],
  );

  return datatableConf;
};

type ArtworkToExhibitionProps = LinksTableProps<ArtworkInExhibition> & {
  artwork: Artwork | undefined;
};

// Expositions liées à une oeuvre
const ArtworkToExhibition = ({
  id,
  relatedData,
  fields,
  artwork,
  isAddMode,
  columnsLink,
  searchContent,
}: ArtworkToExhibitionProps) => {
  const { t } = useTranslation();
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notAlreadyRelated } = useGetNotRelatedResource<Exhibition>({
    resultType: "exhibition",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const columnsExhibitions: ColumnDef<Exhibition, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "expositionsDescriptionNumeroExpo",
        header: t("tab_expositionsDescriptionNumeroExpo"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <TextCell value={row.original.expositionsDescriptionNumeroExpo} />
          );
        },
      },
      {
        accessorKey: "expositionsDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            row.original.attachments && (
              <ImageCell
                value={"expositionsDescriptionVignette"}
                attachments={row.original.attachments}
                domain={"exhibition"}
              />
            )
          );
        },
      },
      {
        accessorKey: "expositionsDescriptionAnnee",
        header: t("tab_expositionsDescriptionAnnee"),
        size: 10,
        cell: ({ row }) => {
          return <TextCell value={row.original.expositionsDescriptionAnnee} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionTitre",
        header: t("tab_expositionsDescriptionTitre"),
        size: 300,
        cell: ({ row }) => {
          return <HtmlCell value={row.original.expositionsDescriptionTitre} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionDates",
        header: t("tab_expositionsDescriptionDates"),
        cell: ({ row }) => {
          return <TextCell value={row.original.expositionsDescriptionDates} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionLieu",
        header: t("tab_expositionsDescriptionLieu"),
        size: 300,
        cell: ({ row }) => {
          return (
            <div className="flex flex-col items-start text-wrap">
              <p className="text-[14px] font-[500]">
                {row.original.expositionsDescriptionVille}
              </p>
              <p className="text-[14px] font-[400] text-gray-500">
                {row.original.expositionsDescriptionLieu}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          if (!artwork) {
            return null;
          }
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="exhibition"
                id={row.original.id}
                elements={[
                  {
                    label: t("expositionsDescriptionNumeroExpo"),
                    value: row.original.expositionsDescriptionNumeroExpo,
                    type: "text",
                  },
                  {
                    label: t("expositionsDescriptionResume"),
                    value: row.original.expositionsDescriptionResume,
                    type: "richtext",
                  },
                ]}
              />
              <ArtworkInExhibitionDrawer
                key={row.original.id}
                fields={fields}
                queryId={artwork.id}
                type={"artwork"}
                data={{
                  id: "",
                  artwork: artwork,
                  exhibition: row.original,
                  oeuvresDansExpositionsDescriptionNumeroCatalogue: "",
                  oeuvresDansExpositionsNotes: "",
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [artwork, fields, t]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsExhibitions}
          data={notAlreadyRelated?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notAlreadyRelated?.totalSize ?? 0,
          }}
          options={{
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      columnsExhibitions,
      columnsLink,
      getPagination,
      isAddMode,
      notAlreadyRelated?.data,
      notAlreadyRelated?.totalSize,
      relatedData,
    ],
  );

  return datatableConf;
};
